import React, { useCallback, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import SideDrawer from '../../../../../common/_custom/SideDrawer/SideDrawer';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import FinancialLoaderComponent from '../../../../../common/FinancialLoader/FinancialLoaderComponent';
import EditableDropdown from '../../../../../common/EditableDropdown/EditableDropdown';
import { user_outline, office_building } from '../../../../../assets/hostedassets';
import { useAuditorCommentsDrawer, formatYear } from './useAuditorCommentsDrawer';
import { useAuditedType } from '../useAuditedType';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../../utils/constants/commonConstants';
import type { AuditorYearRow } from './auditorCommentsDrawer.types';
import styles from './AuditorCommentsDrawer.module.scss';

interface AuditorCommentsDrawerProps {
    orgId: string;
    onClose: () => void;
}

const AuditorCommentsDrawer: React.FC<AuditorCommentsDrawerProps> = ({ orgId, onClose }) => {
    // `undefined` on initial load (backend auto-resolves); set to the uppercase nature
    // string after the user explicitly picks from the dropdown.
    const [nature, setNature] = useState<string | undefined>(undefined);

    const onConsolidationChange = useCallback((newNature: string) => {
        setNature(newNature);
    }, []);

    const {
        auditedType,
        latestConsolidatedFY,
        latestStandaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
    } = useAuditedType(onConsolidationChange);

    const { loading, rows, lastUpdated, isMcaCalled } = useAuditorCommentsDrawer(orgId, nature, setAuditedTypeFromYears);

    const renderAuditorDetails = (row: AuditorYearRow) => {
        if (!row.auditorName && !row.auditorFirmName) {
            return <span className={styles.NoData}>No Data Available</span>;
        }
        return (
            <>
                {row.auditorName && (
                    <div className={styles.AuditorDetailItem}>
                        <img src={user_outline} alt="" width="16" height="16" />
                        <span>{row.auditorName}</span>
                    </div>
                )}
                {row.auditorFirmName && (
                    <div className={styles.AuditorDetailItem}>
                        <img src={office_building} alt="" width="16" height="16" />
                        <span>{row.auditorFirmName}</span>
                    </div>
                )}
            </>
        );
    };

    const renderQualificationBadge = (hasAdverseRemarks: boolean | null) => {
        if (hasAdverseRemarks == null) return null;
        if (hasAdverseRemarks) {
            return <div className={`${styles.QualificationBadge} ${styles.Qualified}`}>Qualified / Reservations / Adverse Remarks</div>;
        }
        return <div className={`${styles.QualificationBadge} ${styles.NotQualified}`}>No Qualified / Reservations / Adverse Remarks</div>;
    };

    const renderCommentsList = (comments: string[]) => {
        if (!comments.length) return null;
        return (
            <ul className={styles.CommentsList}>
                {comments.map((comment, idx) => (
                    <li key={idx}>{comment}</li>
                ))}
            </ul>
        );
    };

    const renderAuditorComments = (row: AuditorYearRow) => {
        const hasQualification = row.reportHasAdverseRemarks != null;
        const hasComments = row.auditorComments.length > 0;

        if (!hasQualification && !hasComments) {
            return <span className={styles.NoData}>No Data Available</span>;
        }

        return (
            <>
                {renderQualificationBadge(row.reportHasAdverseRemarks)}
                {renderCommentsList(row.auditorComments)}
            </>
        );
    };

    const renderDirectorComments = (row: AuditorYearRow) => {
        const hasBadgeOffset = row.reportHasAdverseRemarks != null;
        if (!row.directorComments.length) {
            return <span className={`${styles.NoData} ${hasBadgeOffset ? styles.BadgeOffset : ''}`}>No Data Available</span>;
        }
        return <div className={hasBadgeOffset ? styles.BadgeOffset : undefined}>{renderCommentsList(row.directorComments)}</div>;
    };

    return (
        <SideDrawer onClose={onClose} width="80%">
            <SideDrawer.Header>
                <div className={styles.HeaderContainer}>
                    <div className={styles.HeaderLeft}>
                        <span className={styles.HeaderTitle}>Auditor Comments</span>
                        {(latestConsolidatedFY !== null || latestStandaloneFY !== null) && (
                            <div className={styles.ConsolidatedDropdown}>
                                <EditableDropdown
                                    id={'AuditorConsolidatedSelector'}
                                    value={auditedType ?? ''}
                                    options={[AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE]}
                                    optionsObject={auditedTypeOptionsObject}
                                    setOutput={handleConsolidationChange}
                                    removeInput={true}
                                    optionStyle={{ fontSize: '14px' }}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.HeaderRight}>
                        {isMcaCalled && (
                            <span className={styles.LoaderMessage}>
                                <LoadingSpinner color="#6021B3" />
                                Fetching latest data from MCA (takes upto 24 hours)
                            </span>
                        )}
                        {lastUpdated && <span className={styles.MetaItem}>Last Updated on {lastUpdated}</span>}
                        {!loading && rows.length > 0 && <span className={styles.MetaValue}>(Source:MCA)</span>}
                    </div>
                </div>
            </SideDrawer.Header>
            <div className={styles.DrawerContent}>
                {loading && (
                    <FinancialLoaderComponent tableName="Auditor Comments" message="Fetching auditor comments data" status={loading} />
                )}
                {!loading && (
                    <table className={styles.Table}>
                        <thead>
                            <tr className={styles.TableHeader}>
                                <th>Year</th>
                                <th>Auditor Details</th>
                                <th>Auditor&apos;s Comments</th>
                                <th>Director&apos;s Comments</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rows.map((row) => (
                                    <tr key={row.year} className={styles.TableRow}>
                                        <td className={styles.YearCell}>{formatYear(row.year)}</td>
                                        <td className={styles.AuditorDetailsCell}>{renderAuditorDetails(row)}</td>
                                        <td className={styles.CommentsCell}>{renderAuditorComments(row)}</td>
                                        <td className={styles.CommentsCell}>{renderDirectorComments(row)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr className={`${styles.TableRow} ${styles.UnavailableDataTableRow}`}>
                                    <td colSpan={4}>
                                        <div className={styles.UnavailableDataRow}>
                                            <InfoIcon className={styles.UnavailableDataIcon} aria-hidden />
                                            <span className={styles.UnavailableDataMessage}>Not able to fetch data</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </SideDrawer>
    );
};

export default AuditorCommentsDrawer;
